import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, Users, Shield, Zap, AlertTriangle, TrendingUp, Server, Database, Wifi, WifiOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils';
import { toast } from 'sonner';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface LiveMetrics {
  activeUsers: number;
  activeSessions: number;
  securityEvents24h: number;
  criticalAlerts: number;
  systemLoad: number;
  responseTime: number;
  errorRate: number;
  lastUpdated: string;
  uptimePercentage: number;
}

interface SecurityEvent {
  id: string;
  event_type: string;
  severity: string;
  created_at: string;
  details?: any;
}

interface PerformanceMetric {
  id: string;
  metric_type: string;
  operation_name: string;
  duration_ms: number;
  status: string;
  recorded_at: string;
}

interface UserSession {
  id: string;
  user_id?: string;
  session_id: string;
  started_at: string;
  last_seen: string;
  is_active: boolean;
  current_page?: string;
  ip_address?: string;
}

export function LiveMetricsDashboard() {
  const [metrics, setMetrics] = useState<LiveMetrics>({
    activeUsers: 0,
    activeSessions: 0,
    securityEvents24h: 0,
    criticalAlerts: 0,
    systemLoad: 0,
    responseTime: 0,
    errorRate: 0,
    lastUpdated: new Date().toISOString(),
    uptimePercentage: 99.9,
  });

  const [recentEvents, setRecentEvents] = useState<SecurityEvent[]>([]);
  const [recentMetrics, setRecentMetrics] = useState<PerformanceMetric[]>([]);
  const [activeSessions, setActiveSessions] = useState<UserSession[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch initial data
  const fetchLiveMetrics = useCallback(async () => {
    try {
      productionLogger.info('Fetching live dashboard metrics');
      
      // Get active sessions
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('is_active', true)
        .order('last_seen', { ascending: false });

      if (sessionsError) {
        productionLogger.error('Error fetching active sessions:', sessionsError);
      } else {
        setActiveSessions((sessions || []) as UserSession[]);
      }

      // Get recent security events
      const { data: events, error: eventsError } = await supabase
        .from('security_events')
        .select('*')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(20);

      if (eventsError) {
        productionLogger.error('Error fetching security events:', eventsError);
      } else {
        setRecentEvents(events || []);
      }

      // Get performance metrics
      const { data: perfMetrics, error: perfError } = await supabase
        .from('performance_metrics')
        .select('*')
        .gte('recorded_at', new Date(Date.now() - 60 * 60 * 1000).toISOString())
        .order('recorded_at', { ascending: false })
        .limit(50);

      if (perfError) {
        productionLogger.error('Error fetching performance metrics:', perfError);
      } else {
        setRecentMetrics(perfMetrics || []);
      }

      // Calculate derived metrics
      const activeSessionCount = sessions?.length || 0;
      const uniqueActiveUsers = new Set(sessions?.filter(s => s.user_id).map(s => s.user_id)).size;
      const criticalEvents = events?.filter(e => e.severity === 'critical').length || 0;
      const avgResponseTime = perfMetrics?.length ? 
        perfMetrics.reduce((acc, m) => acc + Number(m.duration_ms), 0) / perfMetrics.length : 0;
      const errorCount = perfMetrics?.filter(m => m.status === 'error').length || 0;
      const errorRate = perfMetrics?.length ? (errorCount / perfMetrics.length) * 100 : 0;

      setMetrics({
        activeUsers: uniqueActiveUsers,
        activeSessions: activeSessionCount,
        securityEvents24h: events?.length || 0,
        criticalAlerts: criticalEvents,
        systemLoad: Math.random() * 100, // Mock for now
        responseTime: avgResponseTime,
        errorRate: errorRate,
        lastUpdated: new Date().toISOString(),
        uptimePercentage: 99.9 - (errorRate * 0.1),
      });

    } catch (error) {
      productionLogger.error('Error fetching live metrics:', error);
      toast.error('Failed to fetch live metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up real-time subscriptions
  useEffect(() => {
    let securityChannel: RealtimeChannel | null = null;
    let sessionsChannel: RealtimeChannel | null = null;
    let metricsChannel: RealtimeChannel | null = null;

    const setupRealtime = async () => {
      try {
        // Security events real-time
        securityChannel = supabase
          .channel('live-security-events')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'security_events'
            },
            (payload) => {
              const newEvent = payload.new as SecurityEvent;
              productionLogger.info('Live security event received:', newEvent.event_type);
              
              setRecentEvents(prev => [newEvent, ...prev.slice(0, 19)]);
              setMetrics(prev => ({
                ...prev,
                securityEvents24h: prev.securityEvents24h + 1,
                criticalAlerts: newEvent.severity === 'critical' ? prev.criticalAlerts + 1 : prev.criticalAlerts,
                lastUpdated: new Date().toISOString(),
              }));

              if (newEvent.severity === 'critical') {
                toast.error(`🚨 Critical Security Event: ${newEvent.event_type}`);
              }
            }
          )
          .subscribe();

        // User sessions real-time
        sessionsChannel = supabase
          .channel('live-user-sessions')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_sessions'
            },
            (payload) => {
              productionLogger.info('Live session update:', payload.eventType);
              
              if (payload.eventType === 'INSERT') {
                const newSession = payload.new as UserSession;
                if (newSession.is_active) {
                  setActiveSessions(prev => [newSession, ...prev]);
                  setMetrics(prev => ({
                    ...prev,
                    activeSessions: prev.activeSessions + 1,
                    lastUpdated: new Date().toISOString(),
                  }));
                }
              } else if (payload.eventType === 'UPDATE') {
                const updatedSession = payload.new as UserSession;
                setActiveSessions(prev => 
                  prev.map(session => 
                    session.id === updatedSession.id ? updatedSession : session
                  ).filter(session => session.is_active)
                );
              }
            }
          )
          .subscribe();

        // Performance metrics real-time
        metricsChannel = supabase
          .channel('live-performance-metrics')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'performance_metrics'
            },
            (payload) => {
              const newMetric = payload.new as PerformanceMetric;
              productionLogger.info('Live performance metric received:', newMetric.metric_type);
              
              setRecentMetrics(prev => [newMetric, ...prev.slice(0, 49)]);
              
              // Update real-time averages
              setMetrics(prev => {
                const recentResponseTimes = [newMetric, ...recentMetrics.slice(0, 9)];
                const avgResponse = recentResponseTimes.reduce((acc, m) => acc + Number(m.duration_ms), 0) / recentResponseTimes.length;
                const errors = recentResponseTimes.filter(m => m.status === 'error').length;
                const errorRate = (errors / recentResponseTimes.length) * 100;

                return {
                  ...prev,
                  responseTime: avgResponse,
                  errorRate: errorRate,
                  lastUpdated: new Date().toISOString(),
                };
              });
            }
          )
          .subscribe();

        setIsConnected(true);
        productionLogger.info('Real-time subscriptions established');

      } catch (error) {
        productionLogger.error('Error setting up real-time subscriptions:', error);
        setIsConnected(false);
      }
    };

    setupRealtime();
    fetchLiveMetrics();

    // Set up periodic refresh for metrics that might not have real-time events
    const refreshInterval = setInterval(fetchLiveMetrics, 30000); // 30 seconds

    return () => {
      if (securityChannel) supabase.removeChannel(securityChannel);
      if (sessionsChannel) supabase.removeChannel(sessionsChannel);
      if (metricsChannel) supabase.removeChannel(metricsChannel);
      clearInterval(refreshInterval);
    };
  }, [fetchLiveMetrics]);

  const getHealthColor = (value: number, type: 'uptime' | 'response' | 'error') => {
    switch (type) {
      case 'uptime':
        return value >= 99.5 ? 'text-primary' : value >= 99 ? 'text-accent' : 'text-destructive';
      case 'response':
        return value <= 200 ? 'text-primary' : value <= 500 ? 'text-accent' : 'text-destructive';
      case 'error':
        return value <= 1 ? 'text-primary' : value <= 5 ? 'text-accent' : 'text-destructive';
      default:
        return 'text-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6" />
          <h2 className="text-2xl font-bold">Live Metrics Dashboard</h2>
          <Badge variant={isConnected ? "default" : "destructive"} className="ml-2">
            {isConnected ? <><Wifi className="h-3 w-3 mr-1" /> Live</> : <><WifiOff className="h-3 w-3 mr-1" /> Offline</>}
          </Badge>
        </div>
        <div className="text-sm text-muted-foreground">
          Last updated: {new Date(metrics.lastUpdated).toLocaleTimeString()}
        </div>
      </div>

      {/* Real-time Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold">{metrics.activeUsers}</p>
              </div>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Sessions</p>
                <p className="text-2xl font-bold">{metrics.activeSessions}</p>
              </div>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Security Events (24h)</p>
                <p className="text-2xl font-bold">{metrics.securityEvents24h}</p>
              </div>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Critical Alerts</p>
                <p className="text-2xl font-bold text-destructive">{metrics.criticalAlerts}</p>
              </div>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">Uptime</p>
              <p className={`text-2xl font-bold ${getHealthColor(metrics.uptimePercentage, 'uptime')}`}>
                {metrics.uptimePercentage.toFixed(2)}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">Avg Response Time</p>
              <p className={`text-2xl font-bold ${getHealthColor(metrics.responseTime, 'response')}`}>
                {metrics.responseTime.toFixed(0)}ms
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">Error Rate</p>
              <p className={`text-2xl font-bold ${getHealthColor(metrics.errorRate, 'error')}`}>
                {metrics.errorRate.toFixed(1)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Data Tabs */}
      <Tabs defaultValue="events" className="space-y-4">
        <TabsList>
          <TabsTrigger value="events">Live Security Events</TabsTrigger>
          <TabsTrigger value="sessions">Active Sessions</TabsTrigger>
          <TabsTrigger value="performance">Performance Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle>Recent Security Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentEvents.slice(0, 10).map((event) => (
                  <div key={event.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Badge variant={event.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {event.severity}
                      </Badge>
                      <span className="text-sm">{event.event_type}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
                {recentEvents.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No recent security events</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions">
          <Card>
            <CardHeader>
              <CardTitle>Active User Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {activeSessions.slice(0, 10).map((session) => (
                  <div key={session.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-sm">
                        {session.user_id ? 'Authenticated' : 'Guest'} - {session.current_page || 'Unknown'}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(session.last_seen).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
                {activeSessions.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No active sessions</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Recent Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentMetrics.slice(0, 10).map((metric) => (
                  <div key={metric.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2">
                      <Badge variant={metric.status === 'error' ? 'destructive' : 'outline'}>
                        {metric.status}
                      </Badge>
                      <span className="text-sm">{metric.operation_name}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium">{Number(metric.duration_ms).toFixed(0)}ms</span>
                      <div className="text-xs text-muted-foreground">
                        {new Date(metric.recorded_at).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
                {recentMetrics.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No recent performance metrics</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}